/**
 * eventListing — UI state for the bromo/event-listing block.
 *
 * The actual querying/pagination is server-side (HTMX → wp_ajax_bromo_event_listing).
 * This component only manages: the date controls (presets, custom range, prev/next
 * window stepping), the facet dropdowns, and the mobile filter drawer. Filter
 * values are mirrored into hidden inputs (x-bind) that the form serializes, so a
 * commit is just "flush Alpine, then fire the form's `refresh` trigger".
 *
 * Desktop controls commit immediately. The mobile drawer stages changes
 * (deferred = true) and commits once on Apply.
 */

// Local Y-m-d formatter / parser (avoids UTC drift from `new Date('YYYY-MM-DD')`).
const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fromYmd = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

// Default "foundation" window: today → +N days. Keep in sync with the PHP
// default in bromo_query_events() and the SSR label fallback in event-listing.twig.
const DEFAULT_RANGE_DAYS = 10;

document.addEventListener('alpine:init', () => {
  Alpine.data('eventListing', () => ({
    // Date window (empty = default "upcoming from today, open-ended").
    start: '',
    end: '',
    preset: 'upcoming', // upcoming | today | week | month | nextmonth | custom
    dateOpen: false,

    // Facets.
    eventType: '',
    eventTypeLabel: '',
    featured: false,
    cost: '',
    costLabel: '',
    series: '',
    seriesLabel: '',
    // Dropdown state: `open` drives the trigger (aria-expanded + chevron flip);
    // `menuVisible` keeps the panel mounted through the list-dismiss exit stagger.
    open: { type: false, featured: false, cost: false, series: false },
    menuVisible: { type: false, featured: false, cost: false, series: false },

    // Mobile drawer.
    filtersOpen: false,
    deferred: false,

    // slug → display name, read off the rendered options so a deep-linked
    // ev_type / ev_series shows its label without a second data source.
    typeLabels: {},
    seriesLabels: {},
    costLabels: { free: 'Free', paid: 'Paid' },

    init() {
      this.$root.querySelectorAll('[data-ev-type-option]').forEach((btn) => {
        this.typeLabels[btn.dataset.slug] = btn.textContent.trim();
      });
      this.$root.querySelectorAll('[data-ev-series-option]').forEach((btn) => {
        this.seriesLabels[btn.dataset.slug] = btn.textContent.trim();
      });

      // Hydrate UI state from the URL so labels match the server-rendered list.
      const p = new URLSearchParams(window.location.search);
      const urlStart = p.get('ev_start') || '';
      const urlEnd = p.get('ev_end') || '';
      if (urlStart || urlEnd) {
        this.start = urlStart;
        this.end = urlEnd;
        this.preset = 'custom';
      } else {
        // No explicit range → the default foundation window (today → +N days),
        // matching the server default so the label reflects the rendered list.
        const d = this.defaultRange();
        this.start = d.start;
        this.end = d.end;
        this.preset = 'default';
      }
      this.eventType = p.get('ev_type') || '';
      this.eventTypeLabel = this.eventType ? (this.typeLabels[this.eventType] || '') : '';
      this.featured = p.get('ev_featured') === '1';
      this.cost = p.get('ev_cost') || '';
      this.costLabel = this.cost ? (this.costLabels[this.cost] || '') : '';
      this.series = p.get('ev_series') || '';
      this.seriesLabel = this.series ? (this.seriesLabels[this.series] || '') : '';

      // Show the global, full-viewport loader while any fetch is in flight —
      // filter refresh, search, or pagination. Every request in this block targets
      // #event-listing-results, so its HTMX lifecycle events bubble to this section
      // root. Count in-flight requests so overlapping fetches (fast successive
      // filter changes) don't hide the loader early.
      let inflight = 0;
      this.$root.addEventListener('htmx:beforeRequest', () => {
        if (inflight++ === 0) Alpine.store('loader').show();
      });
      this.$root.addEventListener('htmx:afterRequest', () => {
        if (--inflight <= 0) {
          inflight = 0;
          Alpine.store('loader').hide();
        }
      });
    },

    // --- Commit -------------------------------------------------------------
    // $nextTick lets Alpine flush the x-bound hidden inputs before HTMX
    // serializes the form.
    commit() {
      this.$nextTick(() => {
        if (window.htmx) {
          window.htmx.trigger(this.$refs.filters, 'refresh');
        }
      });
    },
    maybeCommit() {
      if (!this.deferred) this.commit();
    },

    // --- Date controls ------------------------------------------------------
    // The default "foundation" window shown when no explicit range is chosen.
    defaultRange() {
      const today = startOfToday();
      return { start: toYmd(today), end: toYmd(addDays(today, DEFAULT_RANGE_DAYS)) };
    },

    setPreset(name) {
      const today = startOfToday();
      let s = today;
      let e = today;

      if (name === 'today') {
        s = today;
        e = today;
      } else if (name === 'week') {
        e = new Date(today);
        e.setDate(e.getDate() + 6);
      } else if (name === 'month') {
        e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      } else if (name === 'nextmonth') {
        s = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        e = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      }

      this.start = toYmd(s);
      this.end = toYmd(e);
      this.preset = name;
      this.dateOpen = false;
      this.maybeCommit();
    },

    onCustomDate() {
      this.preset = 'custom';
      this.maybeCommit();
    },

    // Prev/next: shift the active window by its own length. With no active
    // window, seed to the current calendar month first, then step.
    stepWindow(dir) {
      let s = this.start ? fromYmd(this.start) : null;
      let e = this.end ? fromYmd(this.end) : null;

      if (!s || !e) {
        const now = new Date();
        s = new Date(now.getFullYear(), now.getMonth(), 1);
        e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      }

      const days = Math.round((e - s) / 86400000) + 1;
      s.setDate(s.getDate() + dir * days);
      e.setDate(e.getDate() + dir * days);

      this.start = toYmd(s);
      this.end = toYmd(e);
      this.preset = 'custom';
      this.maybeCommit();
    },

    resetToday() {
      const d = this.defaultRange();
      this.start = d.start;
      this.end = d.end;
      this.preset = 'default';
      this.dateOpen = false;
      this.maybeCommit();
    },

    rangeLabel() {
      // Open-ended default — upcoming from today, no explicit bounds.
      if (!this.start && !this.end) return 'Upcoming';

      const todayYmd = toYmd(startOfToday());
      // MM/DD/YYYY (Figma), collapsing an endpoint that is the current date to "Today".
      const fmtFull = (str) =>
        new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(fromYmd(str));
      const label = (str) => (str === todayYmd ? 'Today' : fmtFull(str));

      if (this.start && this.end) {
        return this.start === this.end ? label(this.start) : `${label(this.start)} – ${label(this.end)}`;
      }
      if (this.start) return `From ${label(this.start)}`;
      return `Until ${label(this.end)}`;
    },

    // --- Facet panels ------------------------------------------------------
    // A facet renders as a dropdown (≤5 options) or a modal (>5); both share the
    // same open/menuVisible state, only the target element + motion differ.
    // `open` drives the trigger (aria-expanded + chevron); `menuVisible` keeps
    // the panel mounted through its exit animation. Panel handlers own open/close
    // (+ motion); the setters below only mutate filter state. The mobile drawer
    // calls the setters directly (deferred, no panel).

    // Dropdown — nav-style list stagger (list-reveal / list-dismiss on the <ul>).
    openMenu(name, el) {
      this.open[name] = true;
      this.menuVisible[name] = true;
      this.$nextTick(() => { if (el) this.$motion.run('list-reveal', el); });
    },
    closeMenu(name, el = null) {
      if (!this.menuVisible[name]) return;
      this.open[name] = false;
      if (el) {
        this.$motion.run('list-dismiss', el).then(() => { this.menuVisible[name] = false; });
      } else {
        this.menuVisible[name] = false;
      }
    },
    toggleMenu(name, el) {
      this.open[name] ? this.closeMenu(name, el) : this.openMenu(name, el);
    },

    // Modal — coordinated overlay + card (modal-reveal / modal-dismiss on the root).
    openModal(name, el) {
      this.open[name] = true;
      this.menuVisible[name] = true;
      this.$nextTick(() => { if (el) this.$motion.run('modal-reveal', el); });
    },
    closeModal(name, el = null) {
      if (!this.menuVisible[name]) return;
      this.open[name] = false;
      if (el) {
        this.$motion.run('modal-dismiss', el).then(() => { this.menuVisible[name] = false; });
      } else {
        this.menuVisible[name] = false;
      }
    },
    toggleModal(name, el) {
      this.open[name] ? this.closeModal(name, el) : this.openModal(name, el);
    },

    // --- Facets (state only; the panel handlers close + animate) ------------
    setType(slug, label) {
      this.eventType = slug;
      this.eventTypeLabel = label;
      this.maybeCommit();
    },
    clearType() {
      this.eventType = '';
      this.eventTypeLabel = '';
      this.maybeCommit();
    },
    setFeatured(value) {
      this.featured = value;
      this.maybeCommit();
    },
    setCost(bucket, label) {
      this.cost = bucket;
      this.costLabel = label;
      this.maybeCommit();
    },
    clearCost() {
      this.cost = '';
      this.costLabel = '';
      this.maybeCommit();
    },
    setSeries(slug, label) {
      this.series = slug;
      this.seriesLabel = label;
      this.maybeCommit();
    },
    clearSeries() {
      this.series = '';
      this.seriesLabel = '';
      this.maybeCommit();
    },

    // --- Mobile drawer ------------------------------------------------------
    openFilters() {
      this.deferred = true;
      this.filtersOpen = true;
    },
    closeFilters() {
      this.filtersOpen = false;
      this.deferred = false;
    },
    applyFilters() {
      this.filtersOpen = false;
      this.deferred = false;
      this.commit();
    },
    clearFilters() {
      // Reset staged filters; stays deferred until the user taps Apply.
      this.eventType = '';
      this.eventTypeLabel = '';
      this.featured = false;
      this.cost = '';
      this.costLabel = '';
      this.series = '';
      this.seriesLabel = '';
      const d = this.defaultRange();
      this.start = d.start;
      this.end = d.end;
      this.preset = 'default';
    },
  }));
});
